import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  UpdateProjectLocaleKeyDataParams,
  UpdateProjectLocaleKeyDataBody,
  UpdateProjectLocaleKeyDataData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface UpdateProjectLocaleKeyDataState
  extends MergeRequestState<UpdateProjectLocaleKeyDataData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.updateProjectLocaleKeyData,
  isFetching: (state: ReduxState) =>
    state.server.updateProjectLocaleKeyData.isFetching,
  data: (state: ReduxState) => state.server.updateProjectLocaleKeyData.data,
  error: (state: ReduxState) => state.server.updateProjectLocaleKeyData.error,
};

const initialState: UpdateProjectLocaleKeyDataState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/updateProjectLocaleKeyData';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      body,
      config,
    }: {
      params: UpdateProjectLocaleKeyDataParams;
      body: UpdateProjectLocaleKeyDataBody;
      config?: ServerApiConfig;
    },
    { rejectWithValue },
  ) =>
    serverApi
      .updateProjectLocaleKeyData(params, body, config)
      .catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerUpdateProjectLocaleKeyData {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const updateProjectLocaleKeyData: ServerUpdateProjectLocaleKeyData = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
